import { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { apiClient } from "../services/api";
import PageLayout from "../components/PageLayout";
import { renderClassBadges } from "../utils/classBadgeUtils";
import TableInput from "../components/TableInput";
import Dropdown from "../components/Dropdown";
import Badge from "../components/Badge";
import RoundBox from "../components/RoundBox";
import { GRADE_FILTER_OPTIONS, GRADE_ORDER } from "../components/Grade";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  emptyRowStyle,
} from "../components/Style";
import { type Student } from "../types/Student";

type StudentRow = Student;

const columnHelper = createColumnHelper<StudentRow>();

const ACTION_COLORS: Record<string, string> = {
  checkin: "#1f883d", // 초록
  checkout: "#2466c2", // 파랑
  tardy: "#84cc16", // 연두
  earlyLeave: "#0ea5e9", // 밝은 파랑
  absent: "#f97316", // 주황
};

// 로컬 시간을 datetime-local input 형식으로 변환
const getLocalDateTimeString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export default function AttendanceEditPage() {
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [loadingStudent, setLoadingStudent] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error" | ""; text: string }>({
    type: "",
    text: "",
  });
  const [customTimestamp, setCustomTimestamp] = useState<string>(() => {
    return getLocalDateTimeString();
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [list, cls] = await Promise.all([
          apiClient.getStudents(),
          apiClient.getClasses(),
        ]);
        setStudents(list);
        setClasses(cls);
      } catch (error) {
        console.error("학생/반 목록 불러오기 실패:", error);
        setMessage({ type: "error", text: "학생 또는 반 목록을 불러오지 못했습니다." });
      }
    };
    load();
  }, []);

  // 반 목록 - DB의 클래스 테이블에서 가져옴
  const classList = useMemo(() => {
    if (classes.length === 0) {
      const set = new Set(students.flatMap((s) => s.classes?.map(c => c.className) || []).filter(Boolean));
      return Array.from(set).sort();
    }
    return classes.map((c) => c.className).sort();
  }, [classes, students]);

  const updateAttendance = useCallback(async (student: StudentRow, attendance: string) => {
    setLoadingStudent(student.studentNumber);
    setMessage({ type: "", text: "" });
    try {
      // datetime-local 값을 KST로 해석하여 ISO 문자열 생성
      const kstTimestamp = customTimestamp + ':00+09:00'; // KST 타임존 명시
      await apiClient.setAttendanceStatus(student.studentNumber, attendance, kstTimestamp);
      // attendance는 이제 students 테이블이 아닌 history에만 기록됨
      const formattedTime = new Date(customTimestamp).toLocaleString('ko-KR', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: 'Asia/Seoul'
      });
      setMessage({ type: "success", text: `${student.name} 학생 ${attendance} 처리 완료 (${formattedTime})` });
    } catch (error) {
      console.error("출결 수정 실패:", error);
      setMessage({ type: "error", text: "출결 수정에 실패했습니다." });
    } finally {
      setLoadingStudent(null);
    }
  }, [customTimestamp]);

  const renderActionButton = (
    label: string,
    color: string,
    onClick: () => void,
    disabled: boolean,
  ) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "6px 10px",
        borderRadius: 8,
        border: "none",
        color: "#fff",
        background: disabled ? "#d3d7de" : color,
        fontWeight: 600,
        fontSize: "12px",
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: disabled ? "none" : `0 2px 6px ${color}33`,
        transition: "opacity 0.2s",
        opacity: disabled ? 0.7 : 1,
        minWidth: 60,
      }}
    >
      {disabled ? "처리 중" : label}
    </button>
  );

  // 출결 상태 렌더링 함수
  const renderAttendanceStatus = (status: string) => {
    const actualStatus = status || "미등록";
    const colorMap: Record<string, { backgroundColor: string; textColor: string; borderColor: string }> = {
      등원: { backgroundColor: "#f0f9f4", textColor: "#1f883d", borderColor: "#a4dbb5" },
      하원: { backgroundColor: "#f2f6ff", textColor: "#2466c2", borderColor: "#b6e3ff" },
      지각: { backgroundColor: "#f0fdf4", textColor: "#65a30d", borderColor: "#bef264" },
      조퇴: { backgroundColor: "#e0f2fe", textColor: "#0284c7", borderColor: "#7dd3fc" },
      결석: { backgroundColor: "#fff7ed", textColor: "#ea580c", borderColor: "#fdba74" },
      미등록: { backgroundColor: "#f3f4f6", textColor: "#6b7280", borderColor: "#d1d5db" },
    };
    const colors = colorMap[actualStatus] || colorMap["미등록"];
    return (
      <Badge {...colors}>
        {actualStatus}
      </Badge>
    );
  };

  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      columnHelper.accessor("studentNumber", {
        header: "출결번호",
        size: 100,
        cell: (info) => info.getValue(),
        sortingFn: "alphanumeric",
      }),
      columnHelper.accessor("name", {
        header: "이름",
        size: 120,
        cell: (info) => info.getValue(),
        sortingFn: "alphanumeric",
      }),
      columnHelper.accessor("grade", {
        header: "학년",
        size: 80,
        cell: (info) => info.getValue(),
        sortingFn: (rowA, rowB, columnId) => {
          const gradeA = GRADE_ORDER[rowA.getValue(columnId) as string] || 999;
          const gradeB = GRADE_ORDER[rowB.getValue(columnId) as string] || 999;
          if (gradeA !== gradeB) {
            return gradeA - gradeB;
          }
          return rowA.original.name.localeCompare(rowB.original.name);
        },
      }),
      columnHelper.display({
        id: "class",
        header: "반",
        size: 200,
        cell: (info) => {
          const studentClasses = info.row.original.classes || [];
          const classString = studentClasses.map((c: any) => c.className).join(', ');
          return renderClassBadges(classString, classes);
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const classesA = rowA.original.classes || [];
          const classesB = rowB.original.classes || [];
          const stringA = classesA.map((c: any) => c.className).join(', ');
          const stringB = classesB.map((c: any) => c.className).join(', ');
          return stringA.localeCompare(stringB);
        },
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const studentClasses = row.original.classes || [];
          return studentClasses.some((c: any) => c.className.includes(filterValue));
        },
      }),
      // attendance 컬럼 제거: 학생 테이블에서 attendance 필드가 제거되었음
      columnHelper.display({
        id: "actions",
        header: "액션",
        size: 300,
        cell: (props) => {
          const row = props.row.original;
          const disabled = loadingStudent === row.studentNumber;
          return (
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              {renderActionButton("등원", ACTION_COLORS.checkin, () => updateAttendance(row, "등원"), disabled)}
              {renderActionButton("하원", ACTION_COLORS.checkout, () => updateAttendance(row, "하원"), disabled)}
              {renderActionButton("결석", ACTION_COLORS.absent, () => updateAttendance(row, "결석"), disabled)}
            </div>
          );
        },
      }),
    ],
    [loadingStudent, updateAttendance, classes]
  );

  // Table instance
  const table = useReactTable({
    data: students,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout title="출결 수정">
      <style>
        {`
          .attendance-edit-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>

      {message.text && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 14px",
            borderRadius: 10,
            background: message.type === "success" ? "#f0f9f4" : "#fff1f0",
            color: message.type === "success" ? "#1f883d" : "#c2410c",
            fontWeight: 600,
          }}
        >
          {message.text}
        </div>
      )}

      <RoundBox label="출결 일시 설정" direction="horizontal" style={{ marginBottom: 16, maxWidth: 600 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
          <label style={{ fontWeight: 600, fontSize: 14, color: "#24292f", minWidth: 80 }}>
            일시
          </label>
          <input
            type="datetime-local"
            value={customTimestamp}
            onChange={(e) => setCustomTimestamp(e.target.value)}
            style={{
              flex: 1,
              padding: "8px 12px",
              borderRadius: 6,
              border: "1px solid #d0d7de",
              fontSize: 14,
            }}
          />
          <button
            onClick={() => {
              setCustomTimestamp(getLocalDateTimeString());
            }}
            style={{
              ...buttonStyle,
              ...buttonBackgroundColor.primary,
              minWidth: 80,
            }}
          >
            NOW
          </button>
        </div>
      </RoundBox>

      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="attendance-edit-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;

                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}

                          {/* Filter row */}
                          <div style={filterContainerStyle}>
                            {columnId === "studentNumber" && (
                              <TableInput
                                value={
                                  (header.column.getFilterValue() as string) ?? ""
                                }
                                onChange={(e) =>
                                  header.column.setFilterValue(e.target.value)
                                }
                                placeholder="검색"
                              />
                            )}
                            {columnId === "name" && (
                              <TableInput
                                value={
                                  (header.column.getFilterValue() as string) ?? ""
                                }
                                onChange={(e) =>
                                  header.column.setFilterValue(e.target.value)
                                }
                                placeholder="검색"
                              />
                            )}
                            {columnId === "grade" && (
                              <Dropdown
                                value={
                                  (header.column.getFilterValue() as string) ?? ""
                                }
                                onChange={(value) =>
                                  header.column.setFilterValue(value || undefined)
                                }
                                options={[
                                  { value: "", label: "전체" },
                                  ...GRADE_FILTER_OPTIONS,
                                ]}
                              />
                            )}
                            {columnId === "class" && (
                              <Dropdown
                                value={
                                  (header.column.getFilterValue() as string) ?? ""
                                }
                                onChange={(value) =>
                                  header.column.setFilterValue(value || undefined)
                                }
                                options={[
                                  { value: "", label: "전체" },
                                  ...classList.map((c) => ({ value: c, label: c })),
                                ]}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={emptyRowStyle}>
                  학생 정보가 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
